import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GamifiedProgress from './GamifiedProgress';

const QuestionCard = ({ question, currentIndex, totalQuestions, onAnswer, targetType }) => {
  const getOptions = () => {
    if (question.type === 'YesNo') {
      return [
        { value: 1, label: 'No', color: 'hover:border-brand-rose hover:bg-brand-rose/5' },
        { value: 2, label: 'Unsure', color: 'hover:border-brand-dust hover:bg-brand-dust/5' },
        { value: 3, label: 'Yes', color: 'hover:border-brand-olive hover:bg-brand-olive/5' }
      ];
    }
    return [
      { value: 1, label: 'Rarely', color: 'hover:border-brand-olive hover:bg-brand-olive/5' },
      { value: 2, label: 'Sometimes', color: 'hover:border-brand-golden hover:bg-brand-golden/5' },
      { value: 3, label: 'Often', color: 'hover:border-brand-rose hover:bg-brand-rose/5' },
      { value: 4, label: 'Very Often', color: 'hover:border-brand-crimson hover:bg-brand-crimson/5' }
    ];
  };

  const options = getOptions();
  const questionText = targetType === 'self' ? question.text : question.textOther;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      <GamifiedProgress 
        current={currentIndex + 1} 
        total={totalQuestions} 
        region={question.region || "Cerebral Cortex"} 
      />

      <motion.div
        key={question.id}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 1.1, y: -20 }}
        className="max-w-3xl w-full bg-brand-floral/80 backdrop-blur-md rounded-[2.5rem] shadow-2xl p-10 border-2 border-brand-dust/50 relative overflow-hidden"
      >
        {/* Subtle background glow */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-brand-crimson/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-brand-golden/5 rounded-full blur-3xl" />

        <div className="relative z-10">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <span className="text-brand-crimson font-bold text-sm tracking-widest uppercase mb-2 block">
              Cognitive Insight {currentIndex + 1}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-coffee leading-tight font-luxe">
              {questionText}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {options.map((option, index) => (
              <motion.button
                key={option.value}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + (index * 0.1) }}
                onClick={() => onAnswer(option.value)}
                className={`group relative p-6 text-left rounded-2xl border-2 border-brand-dust transition-all duration-300 bg-white/40 ${option.color}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-brand-coffee group-hover:text-brand-crimson transition-colors">
                    {option.label}
                  </span>
                  <div className="w-8 h-8 rounded-full border-2 border-brand-dust flex items-center justify-center group-hover:border-brand-crimson group-hover:bg-brand-crimson transition-all">
                    <div className="w-2 h-2 bg-brand-floral rounded-full opacity-0 group-hover:opacity-100" />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Gamified feedback hint */}
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-8 text-brand-coffee/40 text-sm italic font-medium"
      >
        Your answers are helping calibrate your unique neural profile...
      </motion.p>
    </div>
  );
};

export default QuestionCard;